import { z } from "zod"

import {
  isPortalAmount,
  isPortalChecklistFinished,
  isPortalPickList,
  isPortalTableRows,
  type PortalAnswerValue,
  type PortalDraftValue,
} from "./portal-item-answers.js"
import {
  requiredWhenShown,
  type PortalItemShowWhen,
} from "./portal-item-visibility.js"
import type { ReminderSlot } from "./reminder-rules.js"
import { isTransactionQuestionAnswerValid } from "./transaction-questions.js"

export const portalPeriodTypes = ["month", "quarter", "year"] as const

export type PortalPeriodType = (typeof portalPeriodTypes)[number]

export const portalRequestKinds = ["period", "adhoc"] as const

export type PortalRequestKind = (typeof portalRequestKinds)[number]

export const portalRequestItemKinds = [
  "document",
  "text",
  "date",
  "choice",
  "yes_no",
  "accounting_access",
  "external_handoff",
  "transaction_question",
  "signature",
  "amount",
  "table",
  "checklist",
] as const

export type PortalRequestItemKind = (typeof portalRequestItemKinds)[number]

export const portalRequestPeriodModes = [
  "request_period",
  "any",
  "explicit",
] as const

export type PortalRequestPeriodMode = (typeof portalRequestPeriodModes)[number]

export const portalClassificationStatuses = [
  "unclassified",
  "queued",
  "classified",
  "failed",
  "disabled",
  "capped",
] as const

export type PortalClassificationStatus =
  (typeof portalClassificationStatuses)[number]

export const portalMatchedByValues = ["client", "classifier", "firm"] as const

export type PortalMatchedBy = (typeof portalMatchedByValues)[number]

export const portalRequestItemStatuses = [
  "open",
  "received",
  "needs_review",
  "accepted",
] as const

export type PortalRequestItemStatus = (typeof portalRequestItemStatuses)[number]

export type PortalRequestAnswer = Readonly<{
  value: PortalAnswerValue
  /** A transaction question's picked category. `value` then holds the note. */
  categoryId?: string
  /** The currency an amount was given in. */
  currency?: string
  answeredAt: string
  /** `link`: someone with the client link, not signed in. The id is the
      portal link; the name and email are what they typed, never verified. */
  answeredBy: Readonly<{
    kind: "contact" | "firm" | "link"
    id: string
    name?: string
    email?: string
  }>
}>

/** Text the client typed but has not sent. Kept apart from the answer, so
 * the firm never reads a draft as an answer. `savedAt` orders the saves. */
export type PortalRequestItemDraft = Readonly<{
  value: PortalDraftValue
  savedAt: string
  savedBy: string
}>

/** The item kinds that keep a draft while the client types. A checklist
 * and a choice save each tick as the answer, so they keep none. */
export const portalDraftItemKinds = [
  "text",
  "date",
  "amount",
  "table",
] as const satisfies readonly PortalRequestItemKind[]

/**
 * Whether a draft save lands. Saves can arrive out of order, so one older
 * than the stored draft, or older than the answer that cleared the last
 * draft, is dropped: the newest text wins.
 */
export const shouldApplyItemDraft = (input: {
  savedAt: string
  draft: Pick<PortalRequestItemDraft, "savedAt"> | null
  answer: Pick<PortalRequestAnswer, "answeredAt"> | null
}): boolean => {
  const savedAt = Date.parse(input.savedAt)
  if (!Number.isFinite(savedAt)) return false
  const newerThan = (value: string | undefined) =>
    value === undefined || savedAt > Date.parse(value)
  return newerThan(input.draft?.savedAt) && newerThan(input.answer?.answeredAt)
}

/** How the portal shows an item's example: an image in place, anything
 * else as a file to download. */
export const portalExampleKinds = ["image", "file"] as const

export type PortalExampleKind = (typeof portalExampleKinds)[number]

/** Only the formats every browser draws show inline; a HEIC photo or a PDF
 * is a download. */
export const portalExampleKind = (contentType: string): PortalExampleKind =>
  contentType === "image/png" || contentType === "image/jpeg" ? "image" : "file"

/** Where an example stands in the malware scan, as a shared file does: it
 * waits in `scanning`, a clean scan holds it, and a bad one blocks it. */
export const portalItemExampleStatuses = [
  "scanning",
  "held",
  "quarantined",
] as const

export type PortalItemExampleStatus = (typeof portalItemExampleStatuses)[number]

/** The example a reader of an item sees. The firm sees its example in any
 * state, to know it is still being checked or was blocked; the client sees
 * it only once the scan cleared it. */
export const portalExampleFor = <
  T extends Readonly<{ status: PortalItemExampleStatus }>,
>(
  example: T | null,
  audience: "firm" | "client"
): T | null =>
  example && (audience === "firm" || example.status === "held") ? example : null

/**
 * The thread shown on an item, to the client and to the firm alike: its
 * open thread with the newest message, or the newest one when all were
 * marked resolved. A message on a resolved thread opens it again, so an item
 * keeps one thread in use. The server's itemThreadOrder picks the same one,
 * so a send-back or a help message lands on the thread both sides show.
 */
export const portalItemThreadFor = <
  T extends Readonly<{
    requestItemId: string | null
    resolvedAt: string | null
    lastMessageAt: string | null
  }>,
>(
  threads: readonly T[],
  itemId: string
): T | null => {
  const onItem = threads
    .filter((thread) => thread.requestItemId === itemId)
    .sort((left, right) =>
      (right.lastMessageAt ?? "").localeCompare(left.lastMessageAt ?? "")
    )
  return onItem.find((thread) => !thread.resolvedAt) ?? onItem[0] ?? null
}

/** What the client reads when the firm sends an item back: the firm's own
 * words, or a plain ask when it wrote none. */
export const sendBackReason = (itemName: string, note?: string | null) =>
  note?.trim() || `Please send ${itemName} again.`

/** One reminder email per rule it comes from (see ReminderSlot). */
export const portalReminderSendKinds = [
  "reminder_before",
  "reminder_before_2",
  "reminder_before_3",
  "reminder_before_4",
  "reminder_before_5",
  "reminder_due",
  "reminder_after_1",
  "reminder_after_2",
  "reminder_after_3",
  "reminder_after_4",
  "reminder_after_5",
] as const satisfies ReadonlyArray<`reminder_${ReminderSlot}`>

export type PortalReminderSendKind = (typeof portalReminderSendKinds)[number]

/** Reminders scheduled before migration 0080, by position. Rows already
    scheduled with the email provider keep these kinds until they go out
    or a re-plan cancels them. Nothing new is written with them. */
export const legacyPortalReminderSendKinds = [
  "reminder_1",
  "reminder_2",
  "reminder_3",
] as const

export const portalRequestSendKinds = [
  "request",
  ...portalReminderSendKinds,
  ...legacyPortalReminderSendKinds,
  /* The firm's "sent everything" email, held a short while so an undo can
     cancel it. One per recipient per done event. */
  "client_done",
] as const

export type PortalRequestSendKind = (typeof portalRequestSendKinds)[number]

export const portalReminderSendKind = (
  slot: ReminderSlot
): PortalReminderSendKind => `reminder_${slot}`

export const isPortalReminderSendKind = (kind: string): boolean =>
  (portalReminderSendKinds as readonly string[]).includes(kind) ||
  (legacyPortalReminderSendKinds as readonly string[]).includes(kind)

export type PortalRequestFileState = Readonly<{
  id?: string
  requestItemId: string | null
  status: string
  classifiedType?: string | null
  classifiedPeriod?: string | null
  classifiedAccountHint?: string | null
  classificationConfidence?: number | null
  classificationStatus?: PortalClassificationStatus | null
  matchedBy?: PortalMatchedBy | null
  createdAt?: Date
}>

export type PortalRequestItemState = Readonly<{
  id: string
  required: boolean
  kind?: PortalRequestItemKind
  expectedTypes?: readonly string[]
  accountHint?: string | null
  periodMode?: PortalRequestPeriodMode
  explicitPeriod?: string | null
  requestPeriod?: string | null
  allowMultiple?: boolean
  /** The choices, columns or to-dos, on the kinds that have a list. */
  options?: readonly string[]
  answer?: PortalRequestAnswer | null
  acceptedAt?: Date | string | null
  acceptedBy?: string | null
  /** A transaction question's live rows and when it closed. Absent on other
      kinds, and on a question read without them. */
  questionLiveRowCount?: number | null
  questionClosedAt?: Date | string | null
  /** Set when the listed signer signed the item's current letter. */
  signatureSignedAt?: Date | string | null
  /** True when the item's current letter is voided: the firm canceled it,
   * or its signer was removed. Nothing is left for the client to do. */
  signatureVoided?: boolean
  /** Shows the item only after an earlier answer; see portal-item-visibility. */
  showWhen?: PortalItemShowWhen | null
  /** Why the firm sent the item back, until the client answers again. */
  rejectionReason?: string | null
  /** What the client said about accounting access or a link to another
      site: "I finished this step" is reportedComplete. */
  actionResponse?: { reportedComplete?: boolean } | null
}>

type PortalDateParts = { year: number; month: number; day: number }

const parseDate = (date: string): PortalDateParts => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!match) throw new Error("The period date must use YYYY-MM-DD.")
  const [, year, month, day] = match
  return { year: Number(year), month: Number(month), day: Number(day) }
}

const dateString = (date: Date): string => date.toISOString().slice(0, 10)

const utcDate = (date: string): Date => {
  const { year, month, day } = parseDate(date)
  return new Date(Date.UTC(year, month - 1, day))
}

const addUtcMonths = (date: string, months: number): string => {
  const value = utcDate(date)
  value.setUTCMonth(value.getUTCMonth() + months)
  return dateString(value)
}

export const portalPeriodEnd = (
  periodType: PortalPeriodType,
  periodStart: string
): string => {
  const months = periodType === "month" ? 1 : periodType === "quarter" ? 3 : 12
  const next = utcDate(addUtcMonths(periodStart, months))
  next.setUTCDate(next.getUTCDate() - 1)
  return dateString(next)
}

export const portalPeriodLabel = (
  periodType: PortalPeriodType,
  periodStart: string
): string => {
  const start = utcDate(periodStart)
  const year = start.getUTCFullYear()
  if (periodType === "year") return String(year)
  if (periodType === "quarter") {
    return `Q${Math.floor(start.getUTCMonth() / 3) + 1} ${year}`
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(start)
}

export const nextPortalPeriodStart = (
  periodType: PortalPeriodType,
  periodStart: string
): string =>
  addUtcMonths(
    periodStart,
    periodType === "month" ? 1 : periodType === "quarter" ? 3 : 12
  )

export const previousPortalPeriodStart = (
  periodType: PortalPeriodType,
  periodStart: string
): string =>
  addUtcMonths(
    periodStart,
    periodType === "month" ? -1 : periodType === "quarter" ? -3 : -12
  )

const usablePortalFileStatuses = new Set([
  "held",
  "processing",
  "processed",
  "needs_credits",
])

/** Whether a client may upload to the item: a document, a statement sent in
    place of accounting access, or a receipt under a transaction question. */
export const portalRequestItemTakesFiles = (item: {
  kind: PortalRequestItemKind
  actionResponse?: { provider?: string } | null
}): boolean =>
  item.kind === "document" ||
  item.kind === "transaction_question" ||
  (item.kind === "accounting_access" &&
    (item.actionResponse?.provider === "other" ||
      item.actionResponse?.provider === "none"))

export const isPortalRequestFileReceived = (
  file: PortalRequestFileState
): boolean => usablePortalFileStatuses.has(file.status)

/** The kinds a client answers in the portal, rather than with a file, a
 * signature or a step outside it. */
export const portalAnswerItemKinds = [
  "text",
  "date",
  "choice",
  "yes_no",
  "transaction_question",
  "amount",
  "table",
  "checklist",
] as const satisfies readonly PortalRequestItemKind[]

export const isPortalRequestAnswerValueValid = (input: {
  kind: PortalRequestItemKind
  value: PortalAnswerValue
  options?: readonly string[]
  allowMultiple?: boolean
  categoryId?: string | null
}): boolean => {
  const options = input.options ?? []
  // A signature item is done by signing in the portal, never by an answer.
  if (input.kind === "document" || input.kind === "signature") return false
  if (input.kind === "transaction_question")
    return isTransactionQuestionAnswerValid(input)
  if (input.kind === "yes_no") return typeof input.value === "boolean"
  if (
    input.kind === "checklist" ||
    (input.kind === "choice" && input.allowMultiple)
  )
    return isPortalPickList(input.value, options)
  if (input.kind === "table") return isPortalTableRows(input.value, options)
  const parsedValue = z.string().max(2000).safeParse(input.value)
  if (!parsedValue.success) return false
  const value = parsedValue.data
  if (input.kind === "text") return value.trim().length > 0
  if (input.kind === "choice") return options.includes(value)
  if (input.kind === "amount") return isPortalAmount(value)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const parsed = new Date(`${value}T00:00:00.000Z`)
  return (
    Number.isFinite(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  )
}

/** The firm sent the item's answer back: a new answer clears the reason. */
export const isPortalAnswerSentBack = (
  item: Pick<PortalRequestItemState, "rejectionReason">
): boolean => Boolean(item.rejectionReason?.trim())

/** The kinds whose answer a firm can send back on a regular request: every
 * kind the client answers, except a transaction question, which the firm
 * decides row by row. */
export const portalSendBackAnswerKinds = portalAnswerItemKinds.filter(
  (kind) => kind !== "transaction_question"
)

/**
 * Whether the item's answer finishes it: a valid answer, and on a checklist
 * every to-do ticked. A question's answer is read by the question's own
 * rule, since the firm still has to accept it.
 */
export const isPortalAnswerComplete = (
  item: Pick<
    PortalRequestItemState,
    "kind" | "answer" | "options" | "allowMultiple"
  >
): boolean => {
  if (!item.answer) return false
  const kind = item.kind ?? "document"
  if (kind === "transaction_question")
    return isTransactionQuestionAnswerValid(item.answer)
  const options = item.options ?? []
  return (
    isPortalRequestAnswerValueValid({
      kind,
      value: item.answer.value,
      options,
      allowMultiple: item.allowMultiple,
    }) &&
    (kind !== "checklist" ||
      isPortalChecklistFinished(item.answer.value, options))
  )
}

/**
 * The month a period request asks for, as YYYY-MM, or null for an ad hoc
 * request. An item read for its status needs this as `requestPeriod`:
 * without it a "request period" item accepts a file from any month, or one
 * with no month at all.
 */
export const portalRequestPeriodKey = (request: {
  kind: string
  periodStart: string | null
}): string | null =>
  request.kind === "period" ? (request.periodStart?.slice(0, 7) ?? null) : null

const expectedPeriod = (item: PortalRequestItemState): string | null => {
  if (item.periodMode === "any") return null
  if (item.periodMode === "explicit") return item.explicitPeriod ?? null
  return item.requestPeriod ?? null
}

const classificationAgrees = (
  item: PortalRequestItemState,
  file: PortalRequestFileState
): boolean => {
  const expectedTypes = item.expectedTypes ?? []
  const typeAgrees =
    expectedTypes.length === 0 ||
    Boolean(file.classifiedType && expectedTypes.includes(file.classifiedType))
  const period = expectedPeriod(item)
  const periodAgrees = !period || file.classifiedPeriod === period
  return typeAgrees && periodAgrees
}

const classificationUnavailable = (file: PortalRequestFileState): boolean =>
  file.classificationStatus === "disabled" ||
  file.classificationStatus === "capped" ||
  file.classificationStatus === "failed"

export const portalRequestItemStatus = (
  item: PortalRequestItemState,
  files: readonly PortalRequestFileState[]
): PortalRequestItemStatus => {
  // The client's answer is a proposal that waits for the firm, so it is
  // received, not accepted. A closed question or one with no live rows left
  // is done on both sides.
  if (item.kind === "transaction_question") {
    const settled =
      Boolean(item.questionClosedAt) || item.questionLiveRowCount === 0
    if (settled) return "accepted"
    return isPortalAnswerComplete(item) ? "received" : "open"
  }
  if (item.kind === "signature") {
    return item.signatureSignedAt ? "accepted" : "open"
  }
  // Accounting access and a link are steps the client takes somewhere else
  // and then reports. The firm checks the step, so a report (or a statement
  // sent in place of access) is received, not accepted.
  if (item.kind === "accounting_access" || item.kind === "external_handoff") {
    if (item.acceptedAt || item.acceptedBy) return "accepted"
    if (item.rejectionReason) return "open"
    const statementSent =
      item.kind === "accounting_access" &&
      files.some(
        (file) =>
          file.requestItemId === item.id && isPortalRequestFileReceived(file)
      )
    return item.actionResponse?.reportedComplete || statementSent
      ? "received"
      : "open"
  }
  if ((item.kind ?? "document") !== "document") {
    // An answer the firm sent back is the client's to give again.
    if (isPortalAnswerSentBack(item)) return "open"
    return isPortalAnswerComplete(item) ? "accepted" : "open"
  }

  const receivedFiles = files.filter(
    (file) =>
      file.requestItemId === item.id && isPortalRequestFileReceived(file)
  )
  if (receivedFiles.length === 0) return "open"
  if (item.acceptedAt || item.acceptedBy) return "accepted"

  const hasDisagreement = receivedFiles.some(
    (file) =>
      file.classificationStatus === "classified" &&
      !classificationAgrees(item, file)
  )
  if (hasDisagreement) return "needs_review"

  const allAccepted = receivedFiles.every(
    (file) =>
      classificationUnavailable(file) ||
      (file.classificationStatus === "classified" &&
        classificationAgrees(item, file))
  )
  return allAccepted ? "accepted" : "received"
}

/**
 * True when an item waits on the client: it is open, and it is not a letter
 * the client can no longer sign. A canceled letter stays open until the firm
 * sends a new one or removes the item, but reminders must not ask the client
 * to sign it.
 */
export const isPortalRequestItemWaitingOnClient = (
  item: PortalRequestItemState,
  files: readonly PortalRequestFileState[]
): boolean =>
  portalRequestItemStatus(item, files) === "open" &&
  !(item.kind === "signature" && item.signatureVoided)

/**
 * The required items still waiting on the client: shown, required and open
 * by `portalRequestItemStatus`. A canceled letter is not the client's to do.
 * "I've sent everything" is taken only when none are left, so every kind
 * counts the way its status does: an accounting access or link step the
 * client reported, a signed letter, a file or an answer.
 */
export const portalRequiredItemsWaitingOnClient = <
  T extends PortalRequestItemState,
>(
  items: readonly T[],
  files: readonly PortalRequestFileState[]
): T[] =>
  requiredWhenShown(items).filter(
    (item) => item.required && isPortalRequestItemWaitingOnClient(item, files)
  )

/** Compatibility helper for reminder logic. Satisfaction means received. */
export const isPortalRequestItemSatisfied = (
  itemId: string,
  files: readonly PortalRequestFileState[]
): boolean =>
  files.some(
    (file) => file.requestItemId === itemId && isPortalRequestFileReceived(file)
  )

export type PortalRequestProgress = {
  received: number
  accepted: number
  required: number
}

export const portalRequestProgress = (
  items: readonly PortalRequestItemState[],
  files: readonly PortalRequestFileState[]
): PortalRequestProgress => {
  const requiredItems = requiredWhenShown(items).filter((item) => item.required)
  const statuses = requiredItems.map((item) =>
    portalRequestItemStatus(item, files)
  )
  return {
    received: statuses.filter((status) => status !== "open").length,
    accepted: statuses.filter((status) => status === "accepted").length,
    required: requiredItems.length,
  }
}

export const hasPortalRequestReceivedAllRequiredItems = (
  items: readonly PortalRequestItemState[],
  files: readonly PortalRequestFileState[]
): boolean => {
  const progress = portalRequestProgress(items, files)
  return progress.received === progress.required
}

export const isPortalRequestComplete = (
  items: readonly PortalRequestItemState[],
  files: readonly PortalRequestFileState[]
): boolean => {
  const progress = portalRequestProgress(items, files)
  return progress.accepted === progress.required
}

export const isPortalRequestOverdue = (input: {
  dueAt: Date | null
  closedAt: Date | null
  complete: boolean
  now: Date
}): boolean =>
  !input.closedAt &&
  !input.complete &&
  input.dueAt !== null && input.now.getTime() > input.dueAt.getTime()

export const portalUnfiledFiles = <T extends PortalRequestFileState>(
  files: readonly T[],
  requestCreatedAt: Date
): T[] =>
  files.filter(
    (file) =>
      file.requestItemId === null &&
      Boolean(file.createdAt && file.createdAt >= requestCreatedAt)
  )

export type PortalMatchCandidate = PortalRequestItemState &
  Readonly<{
    requestId: string
    requestClosed: boolean
    currentFileCount: number
  }>

export type PortalMatchSuggestion = Readonly<{
  requestId: string
  itemId: string
  score: number
  reasons: readonly string[]
}>

const normalizedHint = (value: string | null | undefined): string =>
  value?.trim().toLowerCase() ?? ""

export const rankPortalMatchCandidates = (input: {
  file: PortalRequestFileState
  candidates: readonly PortalMatchCandidate[]
}): PortalMatchSuggestion[] => {
  const confidence = Math.max(
    0,
    Math.min(1, input.file.classificationConfidence ?? 0)
  )
  const fileHint = normalizedHint(input.file.classifiedAccountHint)

  return input.candidates
    .filter(
      (item) =>
        !item.requestClosed &&
        (item.kind ?? "document") === "document" &&
        (item.allowMultiple !== false || item.currentFileCount === 0)
    )
    .map((item) => {
      let score = confidence * 0.15
      const reasons: string[] = []
      const expectedTypes = item.expectedTypes ?? []
      if (
        input.file.classifiedType &&
        expectedTypes.includes(input.file.classifiedType)
      ) {
        score += 0.55
        reasons.push("document_type")
      } else if (expectedTypes.length === 0) {
        score += 0.15
        reasons.push("any_document_type")
      }

      const period = expectedPeriod(item)
      if (!period) {
        score += 0.15
        reasons.push("any_period")
      } else if (input.file.classifiedPeriod === period) {
        score += 0.25
        reasons.push("period")
      }

      const itemHint = normalizedHint(item.accountHint)
      if (itemHint && fileHint && fileHint.includes(itemHint)) {
        score += 0.2
        reasons.push("account")
      }

      return {
        requestId: item.requestId,
        itemId: item.id,
        score: Number(Math.min(1, score).toFixed(4)),
        reasons,
      }
    })
    .filter((suggestion) => suggestion.reasons.length > 0)
    .sort(
      (left, right) =>
        right.score - left.score || left.itemId.localeCompare(right.itemId)
    )
}

export type PortalClassifierMatchChoice = Readonly<{
  match: PortalMatchSuggestion | null
  suggestions: readonly PortalMatchSuggestion[]
}>

export const choosePortalClassifierMatch = (input: {
  file: PortalRequestFileState
  candidates: readonly PortalMatchCandidate[]
  threshold?: number
  tieMargin?: number
}): PortalClassifierMatchChoice => {
  if (input.file.matchedBy === "firm" || input.file.matchedBy === "client") {
    return { match: null, suggestions: [] }
  }

  const suggestions = rankPortalMatchCandidates(input)
  const [first, second] = suggestions
  const threshold = input.threshold ?? 0.72
  const tieMargin = input.tieMargin ?? 0.02
  const tied = Boolean(
    first && second && Math.abs(first.score - second.score) <= tieMargin
  )

  return {
    match: first && first.score >= threshold && !tied ? first : null,
    suggestions,
  }
}
